<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import axios from 'axios'

import ImportColumnsMapping from './Components/ImportColumnsMapping.vue'
import ImportFileReadingOverlay from './Components/ImportFileReadingOverlay.vue'
import ImportFinalize from './Components/ImportFinalize.vue'
import ImportNavigation from './Components/ImportNavigation.vue'
import ImportNavigationTab from './Components/ImportNavigationTab.vue'
import ImportOrganize from './Components/ImportOrganize.vue'
import ImportUploadFile from './Components/ImportUploadFile.vue'

import type { Audience, CustomerImport, ImportValueMapping } from '@/types/types'
import { parseStoreIdsFromQuery } from '@/utils/audienceStoreIds'
import {
  hasPendingValueMapping,
  isCustomerImportFileReadError,
  pollCustomerImportUntilFileReady
} from './composables/useCustomerImportFileReadPolling'

const { t } = useI18n()
const { send } = useEvent()

const getFileReadErrorMessage = (error: unknown): string => {
  if (isCustomerImportFileReadError(error)) {
    if (error.code === 'failed' && error.detail) {
      return error.detail
    }

    return t(`pages.import.importPage.readingFile.errors.${error.code}`)
  }

  return t('pages.import.importPage.errors.unexpectedError')
}

const { fetchMerchantAudiences } = useAudience()
const notification = useNotification()
const router = useRouter()
const route = useRoute()

const isLargeScreen = useMediaQuery('(min-width: 768px)')

const audiences = ref<Audience[]>(null)
const file = ref<File | null>(null)
const currentTab = ref<number>(0)
const navigation = ref<InstanceType<typeof ImportNavigation> | null>(null)
const availableColumnTypes = ref<{ label: string; value: string }[]>([])
const customerImport = ref<CustomerImport | null>(null)
const choices = ref<Record<'store' | 'optin' | 'has_optin_email', { label: string; value: string }[]>>()
const defaultStoreId = ref<string | null>(null)
const scopedStoreIds = ref<number[]>([])
const defaultMerchantSegmentId = ref<number | null>(null)
const defaultOptinSMS = ref<string | null>(null)
const defaultOptinEmail = ref<string | null>(null)
const updatePointsOnExisting = ref(false)
const isLoading = ref(false)
const isFileReading = ref(false)
const isLoadingStoreValues = ref(false)
const fileReadingMode = ref<'convert' | 'analyze' | null>(null)

const requiresSpreadsheetConversion = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase()

  return extension === 'xlsx' || extension === 'ods'
}

const savedMapping = ref<ImportValueMapping[] | null>(null)

const IMPORT_WORKFLOW = readonly(
  ref({
    UPLOAD_FILE: 0,
    MAP_COLUMNS: 1,
    ORGANIZE: 2,
    FINALIZE: 3
  })
)

const isUploadValid = computed(() => {
  return file.value !== null
})

const isMappingValid = computed(() => {
  let isValid = true
  const optinColumn = customerImport.value?.columns.find((column) => column.type === 'optin')
  const storeColumn = customerImport.value?.columns.find((column) => column.type === 'store')
  const optinEmailColumn = customerImport.value?.columns.find((column) => column.type === 'has_optin_email')
  if (
    (!optinColumn && !defaultOptinSMS.value) ||
    (!optinEmailColumn && !defaultOptinEmail.value) ||
    (!storeColumn && !defaultStoreId.value)
  ) {
    isValid = false
  }

  if (
    (optinColumn && !savedMapping.value?.find((mapping) => mapping.type === 'optin')) ||
    (optinEmailColumn && !savedMapping.value?.find((mapping) => mapping.type === 'has_optin_email')) ||
    (storeColumn && !savedMapping.value?.find((mapping) => mapping.type === 'store'))
  ) {
    isValid = false
  }

  customerImport.value?.columns.map((column) => {
    if (column.type !== 'ignore') {
      const existingColumns = customerImport.value?.columns.filter((col) => col.type === column.type)
      if (existingColumns.length > 1) {
        isValid = false
      }
    }
  })

  return isValid
})

const isCurrentStepValid = computed(() => {
  if (currentTab.value === IMPORT_WORKFLOW.value.UPLOAD_FILE) {
    return isUploadValid.value
  } else if (currentTab.value === IMPORT_WORKFLOW.value.MAP_COLUMNS) {
    return isMappingValid.value
  }
  return true
})

const mergeSavedSettings = ({
  refreshedImport,
  settings
}: {
  refreshedImport: CustomerImport
  settings: ImportValueMapping[]
}) => {
  const updatedColumns = refreshedImport.columns.map((column) => {
    const savedSetting = settings.find((setting) => setting.type === column.type)
    if (savedSetting) {
      column.values = savedSetting.values
    }
    return column
  })

  return { ...refreshedImport, columns: updatedColumns }
}

const close = () => {
  if (route.query.audience) {
    router.push({ name: 'AudienceDetail', params: { audienceId: route.query.audience } })
  } else {
    router.push({ name: 'CustomerList' })
  }
}

const uploadFile = async () => {
  resetImportForm()
  fileReadingMode.value = requiresSpreadsheetConversion(file.value.name) ? 'convert' : 'analyze'
  isFileReading.value = true

  try {
    const reader = new FileReader()

    const result = await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file.value)
    })

    const initResponse = await axios.post('/customer-imports/initialize', {
      file: result,
      name: file.value.name
    })

    const importId = initResponse.data.data.customerImport.id
    const response = await pollCustomerImportUntilFileReady(importId)

    availableColumnTypes.value = response.data.data.availableColumnTypes
    customerImport.value = response.data.data.customerImport
    choices.value = response.data.data.choices

    send('segment', 'customer_import.create.mapping.autodetect', {
      customerImportId: response.data.data?.customerImport?.id,
      autodetectedColumns:
        response.data.data?.customerImport?.columns?.filter((column) => column.type !== 'ignore')?.length ?? 0
    })
  } finally {
    isFileReading.value = false
    fileReadingMode.value = null
  }
}

const validateMapping = async ({
  storeId,
  merchantSegmentId,
  optinSMS,
  optinEmail,
  currentImport,
  showOverlay = true
}: {
  storeId: string | null
  merchantSegmentId: number | null
  optinSMS: string | null
  optinEmail: string | null
  currentImport: CustomerImport
  showOverlay?: boolean
}) => {
  const params = new URLSearchParams()
  if (storeId) {
    params.append('storeId', storeId)
  }
  if (merchantSegmentId) {
    params.append('merchantSegmentId', merchantSegmentId.toString())
  }
  if (optinSMS) {
    params.append('hasOptinSms', optinSMS)
  }
  if (optinEmail) {
    params.append('hasOptinEmail', optinEmail)
  }

  const response = await axios.post(`/customer-imports/${currentImport.id}/map-columns?${params.toString()}`, {
    columns: currentImport.columns
  })

  let finalResponse = response
  const fileReadStatus = response.data.data.customerImport.fileReadStatus

  if (
    fileReadStatus === 'reading' ||
    fileReadStatus === 'pending' ||
    hasPendingValueMapping(response.data.data.customerImport.columns)
  ) {
    if (showOverlay) {
      fileReadingMode.value = 'analyze'
      isFileReading.value = true
    }

    try {
      finalResponse = await pollCustomerImportUntilFileReady(currentImport.id, { waitForValueMapping: true })
    } finally {
      if (showOverlay) {
        isFileReading.value = false
        fileReadingMode.value = null
      }
    }
  }

  customerImport.value = savedMapping.value
    ? mergeSavedSettings({
        refreshedImport: finalResponse.data.data.customerImport,
        settings: savedMapping.value
      })
    : finalResponse.data.data.customerImport

  choices.value = finalResponse.data.data.choices
}

const loadMappingChoices = () => {
  if (!customerImport.value) {
    return Promise.resolve()
  }

  isLoadingStoreValues.value = true

  return validateMapping({
    storeId: defaultStoreId.value,
    merchantSegmentId: defaultMerchantSegmentId.value,
    optinSMS: defaultOptinSMS.value,
    optinEmail: defaultOptinEmail.value,
    currentImport: customerImport.value,
    showOverlay: false
  })
    .catch((error) => {
      notification.create('error', {
        text: getFileReadErrorMessage(error),
        timeout: 5000
      })
    })
    .finally(() => {
      isLoadingStoreValues.value = false
    })
}

const validateSettings = async () => {
  await axios.post(`/customer-imports/${customerImport.value.id}/settings`, {
    mapping: savedMapping.value || getMappedValues.value
  })
}

const launchImport = async () => {
  await axios.post(`/customer-imports/${customerImport.value.id}/launch`, {
    updatePointsOnExisting: updatePointsOnExisting.value
  })
}

const handleLaunchImport = async () => {
  try {
    isLoading.value = true
    await launchImport()
    sendOneTimeEvent('customer_import.create.finalize')
    router.push({ name: 'ImportList' })
  } catch {
    send('segment', 'customer_import.create.unexpectedError')
    notification.create('error', {
      text: t('pages.import.importPage.errors.unexpectedError'),
      timeout: 5000
    })
  } finally {
    isLoading.value = false
  }
}

const previousStepText = computed(() => {
  return currentTab.value === IMPORT_WORKFLOW.value.UPLOAD_FILE
    ? t('pages.import.importPage.buttons.close')
    : t('pages.import.importPage.buttons.prev')
})

const nextStepText = computed(() => {
  if (isLoading.value) {
    return `${t('global.words.loading')}...`
  }

  return t('pages.import.importPage.buttons.next')
})

const getMappedValues = computed(() => {
  return customerImport.value?.columns
    ?.filter((column) => column.values.length > 0)
    .map((column) => {
      return {
        type: column.type,
        values: column.values.map((value) => {
          return {
            valueInFile: value.valueInFile,
            mappedValue: value.mappedValue?.toString() ?? null
          }
        })
      }
    })
})

const nextStep = async () => {
  const currentTabValue = currentTab.value
  try {
    isLoading.value = true

    if (currentTabValue === IMPORT_WORKFLOW.value.UPLOAD_FILE) {
      await uploadFile()
      sendOneTimeEvent('customer_import.create.step2')
      navigation.value.next()
      return
    }

    if (currentTabValue !== IMPORT_WORKFLOW.value.FINALIZE) {
      navigation.value.next()
    }

    if (currentTabValue === IMPORT_WORKFLOW.value.MAP_COLUMNS) {
      sendOneTimeEvent('customer_import.create.step3')
    } else if (currentTabValue === IMPORT_WORKFLOW.value.ORGANIZE) {
      await validateMapping({
        storeId: defaultStoreId.value,
        merchantSegmentId: defaultMerchantSegmentId.value,
        optinSMS: defaultOptinSMS.value,
        optinEmail: defaultOptinEmail.value,
        currentImport: customerImport.value
      })
      await validateSettings()
      sendOneTimeEvent('customer_import.create.step4')
    }
  } catch (error) {
    if (currentTabValue === IMPORT_WORKFLOW.value.UPLOAD_FILE) {
      send('segment', 'customer_import.upload.error')
      notification.create('error', {
        text: isCustomerImportFileReadError(error)
          ? getFileReadErrorMessage(error)
          : t('pages.import.importPage.errors.uploadFile'),
        timeout: 5000
      })
    } else {
      send('segment', 'customer_import.create.unexpectedError')

      notification.create('error', {
        text: getFileReadErrorMessage(error),
        timeout: 5000
      })
    }
  } finally {
    isLoading.value = false
  }
}

const previousStep = () => {
  if (currentTab.value === IMPORT_WORKFLOW.value.UPLOAD_FILE) {
    close()
  } else {
    navigation.value?.back()
  }
}

const resetImportForm = () => {
  customerImport.value = null
  availableColumnTypes.value = []
  choices.value = null
  savedMapping.value = null
  defaultStoreId.value = null
  defaultOptinSMS.value = null
  updatePointsOnExisting.value = false
}

const getSegments = async () => {
  const data = await fetchMerchantAudiences({ pagination: { pageSize: 1000, page: 1 }, type: 'static' })
  audiences.value = data.data
}

const eventSent = ref([])

const sendOneTimeEvent = (event: string) => {
  if (!eventSent.value.includes(event)) {
    send('segment', event, {
      customerImportId: customerImport.value?.id
    })
    eventSent.value.push(event)
  }
}

onMounted(async () => {
  sendOneTimeEvent('customer_import.create.step1')

  const audienceId = route.query.audience
  if (audienceId) {
    defaultMerchantSegmentId.value = Number(audienceId)
  }

  const storeIds = parseStoreIdsFromQuery(route.query.storeIds)
  if (storeIds) {
    scopedStoreIds.value = storeIds
    if (storeIds.length === 1) {
      defaultStoreId.value = String(storeIds[0])
    }
  }

  await getSegments()
})
</script>
<template>
  <TheTooSmallScreenPage v-if="!isLargeScreen" @on-go-back="router.push({ name: 'CustomerList' })" />

  <div :class="['h-screen text-foreground', !isLargeScreen ? 'hidden' : '']">
    <ImportFileReadingOverlay v-if="isFileReading" :mode="fileReadingMode ?? 'analyze'" />

    <div class="flex grow flex-col" style="height: calc(100% - 71px)">
      <ImportNavigation ref="navigation" @change-tab="currentTab = $event">
        <ImportNavigationTab
          :title="t('pages.import.importPage.breadcrumbs.initialize')"
          :disabled="isLoading"
          :active="currentTab >= IMPORT_WORKFLOW.UPLOAD_FILE"
        >
          <ImportUploadFile v-model="file" />
        </ImportNavigationTab>
        <ImportNavigationTab
          :title="t('pages.import.importPage.breadcrumbs.mapColumns')"
          :disabled="isLoading && currentTab < IMPORT_WORKFLOW.MAP_COLUMNS"
          :active="currentTab >= IMPORT_WORKFLOW.MAP_COLUMNS"
        >
          <ImportColumnsMapping
            v-model:default-store-id="defaultStoreId"
            v-model:default-optin-s-m-s="defaultOptinSMS"
            v-model:default-optin-email="defaultOptinEmail"
            v-model:customer-import="customerImport"
            v-model:saved-mapping="savedMapping"
            v-model:update-points-on-existing="updatePointsOnExisting"
            :scoped-store-ids="scopedStoreIds"
            :is-loaded="
              !isLoading &&
              !isFileReading &&
              !!customerImport &&
              (customerImport.fileReadStatus === 'ready' || isLoadingStoreValues)
            "
            :available-column-types="availableColumnTypes"
            :choices="choices"
            :is-loading-store-values="isLoadingStoreValues"
            :get-choices="loadMappingChoices"
          />
        </ImportNavigationTab>
        <ImportNavigationTab
          :title="t('pages.import.importPage.breadcrumbs.organize')"
          :disabled="isLoading && currentTab < IMPORT_WORKFLOW.ORGANIZE"
          :active="currentTab >= IMPORT_WORKFLOW.ORGANIZE"
        >
          <ImportOrganize
            v-model:default-merchant-segment-id="defaultMerchantSegmentId"
            :audiences="audiences"
            @audience-created="getSegments"
          />
        </ImportNavigationTab>
        <ImportNavigationTab
          :title="t('pages.import.importPage.breadcrumbs.finalize')"
          :disabled="isLoading && currentTab < IMPORT_WORKFLOW.FINALIZE"
          :active="currentTab >= IMPORT_WORKFLOW.FINALIZE"
        >
          <ImportFinalize :customer-import="customerImport" :is-launching="isLoading" @launch="handleLaunchImport" />
        </ImportNavigationTab>
      </ImportNavigation>
    </div>

    <div class="border-box h-18 flex flex-row gap-4 border-t bg-white p-4">
      <div class="flex grow flex-row items-center">
        <div v-if="customerImport?.columns" class="text-sm font-medium text-foreground-secondary">
          {{
            t('pages.import.importPage.importedColumns', {
              count: customerImport?.columns?.filter((column) => column.type !== 'ignore').length,
              total: customerImport?.columns?.length
            })
          }}
        </div>
      </div>
      <Button
        variant="outline"
        :disabled="isLoading"
        :class="[
          isLoading ? 'border-border! bg-white! text-muted-foreground! opacity-50 hover:text-muted-foreground!' : ''
        ]"
        @click="previousStep"
      >
        {{ previousStepText }}
      </Button>
      <Button
        v-if="currentTab !== IMPORT_WORKFLOW.FINALIZE"
        variant="default-primary"
        :disabled="isLoading || !isCurrentStepValid"
        @click="nextStep"
      >
        {{ nextStepText }}
      </Button>
    </div>
  </div>
</template>
